// Разбор DOM вставленного HTML в плоский список «элементов» (абзацы, картинки, таблицы, код, формулы, списки)
// со строчным содержимым в формате TipTap JSON. Всё оформление (шрифты, цвета, размеры, отступы, mso-стили)
// отбрасывается - остаются только смысловые метки: полужирный, курсив, подчёркивание, индексы, код, ссылка.
// Распознавание структуры СТО (подписи, номера, формулы с номером, «где», примечания…) - в structure.ts.
import type { JSONContent as J } from '@tiptap/core';
import type { Prepared, StyleInfo } from './prep';

export interface ListInfo { level: number; marker: string; listId: string }
export interface ImgRef { src: string; width?: number; height?: number; alt?: string; caption?: J[] }
export interface MathRef { omml?: number; latex?: string; mathml?: string; display: boolean; error?: string; text?: string }

export type Item =
  | { k: 'p'; inl: J[]; text: string; tag: string; hLevel?: number; styleName?: string; caption?: boolean;
      list?: ListInfo; align?: string; mono: boolean; bold: boolean; toc?: boolean; docTitle?: boolean; el: Element }
  | { k: 'img'; img: ImgRef }
  | { k: 'table'; el: HTMLTableElement; cells: Map<Element, Item[]> }
  | { k: 'code'; code: string; lang: string }
  | { k: 'formula'; math: MathRef }
  | { k: 'list'; node: J }
  | { k: 'hr' };

export interface LinkRef { href: string; text: string }

export interface DomResult {
  items: Item[];
  /** все формулы фрагмента: OMML/MathML досчитываются на сервере (latex заполняется на месте) */
  maths: MathRef[];
  links: LinkRef[];
  footnotes: number;
  /** ссылки Википедии [1] -> текст источника (если список литературы вставлен вместе с текстом) */
  wikiRefs: Map<string, string>;
  dropped: { images: number; hidden: number; empty: number };
  /** формулы-объекты Equation 3.0 / MathType (в буфере - только картинка): место под формулу */
  oleEquations: number;
}

type Marks = { bold?: boolean; italic?: boolean; underline?: boolean; sup?: boolean; sub?: boolean; code?: boolean; link?: string };

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'TABLE', 'PRE', 'BLOCKQUOTE',
  'FIGURE', 'FIGCAPTION', 'HR', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'NAV', 'CENTER', 'DL', 'DT', 'DD',
  'ADDRESS', 'DETAILS', 'SUMMARY', 'FORM', 'FIELDSET', 'TBODY', 'THEAD', 'TFOOT', 'TR', 'TD', 'TH', 'CAPTION', 'BODY', 'HTML']);
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'OPTION', 'CANVAS',
  'VIDEO', 'AUDIO', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'HEAD', 'TITLE', 'META', 'LINK', 'MAP', 'AREA']);
const MONO_RE = /courier|consolas|monaco|menlo|lucida console|monospace|source code|fira code|jetbrains mono|cascadia/i;
const INVISIBLE_RE = /[\u00ad\u200b\u200c\u200d\u2060\ufeff\u200e\u200f]/g;

const tagOf = (n: Node) => (n.nodeType === 1 ? (n as Element).tagName.toUpperCase() : '');
const styleOf = (el: Element) => (el.getAttribute('style') || '').replace(/\s+/g, ' ');

function cssVal(style: string, prop: string): string | null {
  const m = style.match(new RegExp('(?:^|;)\\s*' + prop + '\\s*:\\s*([^;]+)', 'i'));
  return m ? m[1].trim().replace(/!important/i, '').trim() : null;
}

function isHidden(el: Element): boolean {
  const st = styleOf(el);
  if (/display\s*:\s*none|visibility\s*:\s*hidden|mso-hide\s*:\s*(all|screen)/i.test(st)) return true;
  if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true' && !el.querySelector('img')) return true;
  const cls = el.getAttribute('class') || '';
  return /\b(mw-editsection|noprint|mw-cite-backlink|reference-accessdate|sr-only|visually-hidden|navbox|mw-jump-link)\b/.test(cls);
}

function numAttr(el: Element, name: string): number | undefined {
  const v = el.getAttribute(name);
  const n = v ? parseFloat(v) : NaN;
  if (!isNaN(n)) return n;
  const st = cssVal(styleOf(el), name);
  const m = st?.match(/([\d.]+)\s*(px|pt)?/);
  if (m) return Number(m[1]) * (m[2] === 'pt' ? 96 / 72 : 1);
  return undefined;
}

// ------------------------------------------------------------------------------------------------ формулы веб-страниц
/** LaTeX из аннотации (KaTeX, MathJax, Википедия): <annotation encoding="application/x-tex">. */
function texAnnotation(el: Element): string | null {
  const a = el.querySelector('annotation[encoding="application/x-tex"], annotation[encoding="TeX"]');
  if (!a) return null;
  let t = (a.textContent || '').trim();
  const m = t.match(/^\{\\displaystyle\s*([\s\S]*)\}$/) || t.match(/^\{\\textstyle\s*([\s\S]*)\}$/);
  if (m) t = m[1].trim();
  return t;
}

/** Элемент - формула веб-страницы/нашего формата? -> описание (или null). */
function mathOf(el: Element): MathRef | null {
  const cls = el.getAttribute('class') || '';
  const dt = el.getAttribute('data-type');
  if (dt === 'formula') return { latex: el.getAttribute('data-latex') || '', display: true };
  if (dt === 'math-inline') return { latex: el.getAttribute('data-latex') || '', display: false };
  if (el.hasAttribute('data-sto-omml')) return { omml: Number(el.getAttribute('data-sto-omml')), display: el.getAttribute('data-display') === '1' };
  if (/\bkatex-display\b/.test(cls) || /\bkatex\b/.test(cls) || /\bmwe-math-element\b/.test(cls)) {
    const latex = texAnnotation(el);
    const display = /\bkatex-display\b/.test(cls) || !!el.querySelector('.mwe-math-fallback-image-display') ||
      el.getAttribute('data-display') === 'block';
    if (latex !== null) return { latex, display };
    const mm = el.querySelector('math');
    if (mm) return { mathml: mm.outerHTML, display };
  }
  if (tagOf(el) === 'MJX-CONTAINER') {
    const mm = el.querySelector('mjx-assistive-mml math, math');
    const display = el.getAttribute('display') === 'true' || el.getAttribute('display') === 'block';
    if (mm) return { mathml: mm.outerHTML, display };
  }
  if (tagOf(el) === 'SCRIPT' && /math\/tex/i.test(el.getAttribute('type') || '')) {
    return { latex: (el.textContent || '').trim(), display: /mode=display/i.test(el.getAttribute('type') || '') };
  }
  if (tagOf(el) === 'MATH') {
    const latex = texAnnotation(el);
    const display = el.getAttribute('display') === 'block' || el.getAttribute('mode') === 'display';
    if (latex !== null) return { latex, display };
    return { mathml: el.outerHTML, display };
  }
  return null;
}

// ------------------------------------------------------------------------------------------------ разбор
export class DomReader {
  res: DomResult = { items: [], maths: [], links: [], footnotes: 0, wikiRefs: new Map(), dropped: { images: 0, hidden: 0, empty: 0 }, oleEquations: 0 };
  private fnTargets = new Map<string, Element>();   // якорь сноски в тексте (id/href) -> элемент текста сноски
  private skip = new Set<Element>();                // контейнеры сносок/литературы Википедии (не текст)
  private styles: Map<string, StyleInfo>;
  private pendingImgs: ImgRef[] = [];
  private keepLinks: boolean;

  constructor(private prep: Prepared, opts: { keepLinks?: boolean } = {}) {
    this.styles = prep.styles;
    this.keepLinks = opts.keepLinks !== false;
  }

  /** Элементы внутри el (ячейка таблицы) - тем же разбором, отдельным списком. */
  fragmentItems(el: Element): Item[] {
    const saved = this.res.items;
    const savedImgs = this.pendingImgs;
    this.res.items = [];
    this.pendingImgs = [];
    this.blocks(el);
    this.flushImages();
    const out = this.res.items;
    this.res.items = saved;
    this.pendingImgs = savedImgs;
    return out;
  }

  read(): DomResult {
    const doc = new DOMParser().parseFromString(this.prep.html, 'text/html');
    this.collectFootnotes(doc.body);
    this.blocks(doc.body);
    this.flushImages();
    return this.res;
  }

  // --- сноски: Word (mso-footnote-id), LibreOffice (sdfootnote), Google Docs (#ftnt), общий случай (sup>a[href=#…])
  private collectFootnotes(body: HTMLElement) {
    for (const d of Array.from(body.querySelectorAll('div[style*="mso-element:footnote-list"], div[style*="mso-element:endnote-list"]'))) this.skip.add(d);
    for (const d of Array.from(body.querySelectorAll('div[style*="mso-element:footnote"], div[style*="mso-element:endnote"]'))) {
      if (d.id) this.fnTargets.set(d.id, d);
    }
    for (const d of Array.from(body.querySelectorAll('div[id^="sdfootnote"], div[id^="sdendnote"]'))) {
      this.fnTargets.set(d.id, d);
      this.skip.add(d);
    }
    // Google Docs и прочие: <a href="#ftnt1" id="ftnt_ref1"> в тексте, в конце - <a href="#ftnt_ref1" id="ftnt1">
    for (const a of Array.from(body.querySelectorAll('a[href^="#"]'))) {
      const id = (a.getAttribute('href') || '').slice(1);
      if (!id || this.fnTargets.has(id)) continue;
      // метка сноски: «1», «[1]», «*» (обычно в <sup>)
      if (!/^\[?\d{1,3}\]?$|^\*{1,3}$/.test((a.textContent || '').trim()) && !a.closest('sup')) continue;
      const target = body.querySelector(`[id="${CSS.escape(id)}"], a[name="${CSS.escape(id)}"]`);
      if (!target || target === a) continue;
      // текст сноски - ПОСЛЕ ссылки на неё (обратные ссылки из списка сносок в текст не считаются)
      if (!(a.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (Array.from(this.skip).some((b) => b.contains(a))) continue;
      const own = a.getAttribute('id') || a.getAttribute('name') || '';
      const back = own && target.closest('p, li, div')?.querySelector(`a[href="#${CSS.escape(own)}"]`);
      if (!back) continue;
      const holder = target.closest('li') || target.closest('p') || target;
      // список литературы Википедии - это не сноски, а источники
      if (holder.closest('ol.references')) continue;
      this.fnTargets.set(id, holder);
      const box = holder.parentElement && holder.parentElement !== body && holder.parentElement.children.length <= 40 &&
        Array.from(holder.parentElement.children).every((c) => c.querySelector('a[href^="#"]')) ? holder.parentElement : holder;
      this.skip.add(box);
    }
    // Википедия: список литературы и ссылки [1]
    for (const ol of Array.from(body.querySelectorAll('ol.references'))) {
      for (const li of Array.from(ol.querySelectorAll(':scope > li[id]'))) {
        const t = ((li.querySelector('.reference-text') || li).textContent || '').replace(/\s+/g, ' ').trim();
        if (t) this.res.wikiRefs.set(li.id, t);
      }
      this.skip.add(ol.closest('.reflist, .references-small, div.mw-references-wrap') || ol);
    }
  }

  private footnoteTarget(a: Element): Element | null {
    const st = styleOf(a);
    const m = st.match(/mso-(?:foot|end)note-id\s*:\s*(\w+)/i);
    if (m) return this.fnTargets.get(m[1]) || null;
    const cls = a.getAttribute('class') || '';
    if (/sdfootnoteanc|sdendnoteanc/.test(cls)) {
      const id = (a.getAttribute('href') || '').replace(/^#/, '').replace(/sym$/, '');
      return this.fnTargets.get(id) || null;
    }
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#')) return this.fnTargets.get(href.slice(1)) || null;
    return null;
  }

  private footnoteNode(target: Element): J {
    const clone = target.cloneNode(true) as Element;
    // убрать обратную ссылку «[1]»/«1» в начале текста сноски
    for (const a of Array.from(clone.querySelectorAll('a'))) {
      const t = (a.textContent || '').trim();
      if (/^\[?\*?\d+\]?\.?$|^\*$/.test(t) || /mso-(foot|end)note-id|sdfootnotesym|sdendnotesym/i.test(styleOf(a) + (a.getAttribute('class') || ''))) a.remove();
      else if ((a.getAttribute('href') || '').startsWith('#')) a.replaceWith(...Array.from(a.childNodes));
    }
    const inl: J[] = [];
    this.inline(clone, {}, inl, true);
    trimInline(inl);
    this.res.footnotes++;
    return { type: 'footnote', attrs: { content: inl } };
  }

  // --- блоки
  private blocks(parent: Element) {
    let buf: Node[] = [];
    const flush = () => {
      if (buf.length) {
        const wrap = parent.ownerDocument.createElement('div');
        for (const n of buf) wrap.appendChild(n.cloneNode(true));
        this.paragraph(wrap, 'P', true);
        buf = [];
      }
    };
    for (const ch of Array.from(parent.childNodes)) {
      if (ch.nodeType === 3) { buf.push(ch); continue; }
      if (ch.nodeType !== 1) continue;
      const el = ch as Element;
      const tag = tagOf(el);
      if (this.skip.has(el) || SKIP_TAGS.has(tag)) continue;
      if (isHidden(el)) { this.res.dropped.hidden++; continue; }
      const math = mathOf(el);
      if (math && math.display && !el.closest('p, li, td, th, h1, h2, h3, h4, h5, h6')) {
        flush();
        this.pushMath(math, true);
        continue;
      }
      if (!BLOCK_TAGS.has(tag) && !hasBlockChild(el) && !isStandaloneImage(el)) { buf.push(el); continue; }
      flush();
      this.block(el, tag);
    }
    flush();
  }

  private block(el: Element, tag: string) {
    switch (tag) {
      case 'H1': case 'H2': case 'H3': case 'H4': case 'H5': case 'H6':
      case 'P': case 'DT': case 'DD': case 'CAPTION': case 'ADDRESS': case 'SUMMARY':
        if (hasBlockChild(el) && tag !== 'P') { this.blocks(el); return; }
        this.paragraph(el, tag);
        return;
      case 'UL': case 'OL':
        this.htmlList(el);
        return;
      case 'LI':
        this.htmlList(el.ownerDocument.createElement('ul').appendChild(el.cloneNode(true)).parentElement!);
        return;
      case 'TABLE': {
        this.flushImages();
        // содержимое ячеек читается сразу (формулы в ячейках должны попасть в общий пакет преобразования)
        const cells = new Map<Element, Item[]>();
        for (const c of tableCells(el as HTMLTableElement)) cells.set(c, this.fragmentItems(c));
        this.res.items.push({ k: 'table', el: el as HTMLTableElement, cells });
        return;
      }
      case 'PRE': {
        this.flushImages();
        // чат-боты и сайты: <pre><div>python</div><code>…</code></pre> - код только из <code>
        const codeEl = el.querySelector('code');
        const code = codeEl ? (codeEl.textContent || '').replace(/\r\n?/g, '\n').replace(/\n+$/, '') : preText(el);
        this.res.items.push({ k: 'code', code, lang: codeLang(el) });
        return;
      }
      case 'HR':
        return;
      case 'FIGURE':
        this.figure(el);
        return;
      case 'DIV':
        if (this.isCodeBox(el)) {
          this.res.items.push({ k: 'code', code: preText(el).replace(/\n+$/, ''), lang: codeLang(el) });
          return;
        }
        if (/\bthumb\b/.test(el.getAttribute('class') || '') && el.querySelector('img')) { this.figure(el); return; }
        if (hasBlockChild(el)) { this.blocks(el); return; }
        this.paragraph(el, 'P');
        return;
      default:
        if (isStandaloneImage(el) || !hasBlockChild(el)) { this.paragraph(el, 'P'); return; }
        this.blocks(el);
    }
  }

  /** Фрагмент кода из браузера/редактора кода: блок с white-space: pre и моноширинным шрифтом (VS Code и т. п.). */
  private isCodeBox(el: Element): boolean {
    const st = styleOf(el);
    return /white-space\s*:\s*pre/i.test(st) && MONO_RE.test(cssVal(st, 'font-family') || '');
  }

  private figure(el: Element) {
    const img = el.querySelector('img');
    const cap = el.querySelector('figcaption, .thumbcaption, .gallerytext');
    if (!img) { this.blocks(el); return; }
    const ref = imgRef(img);
    if (!ref) return;
    if (cap) {
      const inl: J[] = [];
      const c2 = cap.cloneNode(true) as Element;
      c2.querySelectorAll('.magnify').forEach((x) => x.remove());
      this.inline(c2, {}, inl, true);
      trimInline(inl);
      ref.caption = inl;
    }
    this.flushImages();
    this.res.items.push({ k: 'img', img: ref });
  }

  private pushMath(math: MathRef, display: boolean) {
    this.flushImages();
    math.display = display;
    this.res.maths.push(math);
    this.res.items.push({ k: 'formula', math });
  }

  private flushImages() {
    for (const img of this.pendingImgs) this.res.items.push({ k: 'img', img });
    this.pendingImgs = [];
  }

  // --- абзац
  private paragraph(el: Element, tag: string, loose = false) {
    const st = styleOf(el);
    const cls = (el.getAttribute('class') || '').split(/\s+/)[0] || '';
    const sty = this.styles.get(cls);
    const styleName = sty?.name || (cls.startsWith('Mso') ? cls.slice(3).toLowerCase() : '');
    let hLevel: number | undefined;
    if (/^H[1-6]$/.test(tag)) hLevel = Number(tag[1]);
    const hm = styleName.match(/^(?:heading|заголовок)\s*(\d)/);
    if (hm) hLevel = Number(hm[1]);
    if (/^(title|название|заголовок)$/.test(styleName) || cls === 'MsoTitle' || cls === 'MsoSubtitle') hLevel = hLevel || 1;
    const ol = cssVal(st, 'mso-outline-level') || (sty?.outline ? String(sty.outline) : null);
    if (!hLevel && ol && Number(ol) >= 1 && Number(ol) <= 9) hLevel = Number(ol);
    if (!hLevel && el.getAttribute('role') === 'heading') hLevel = Number(el.getAttribute('aria-level')) || 2;
    const caption = /^(caption|подпись|название объекта|подпись рисунка|подпись таблицы)/.test(styleName) || cls === 'MsoCaption' || undefined;
    let list: ListInfo | undefined;
    const ml = st.match(/mso-list\s*:\s*l(\d+)\s+level(\d+)/i);
    let marker = '';
    const inl: J[] = [];
    const ctx: Marks = {};
    if (sty?.mono) ctx.code = true;
    this.inline(el, ctx, inl, false, (m) => { marker = m; });
    if (ml) list = { listId: ml[1], level: Number(ml[2]) - 1, marker: marker.replace(/\s+/g, '') };
    trimInline(inl);
    const text = inlineText(inl);
    const align = (el.getAttribute('align') || cssVal(st, 'text-align') || '').toLowerCase() || undefined;
    const runs = inl.filter((n) => n.type === 'text' && (n.text || '').trim());
    const mono = runs.length > 0 && runs.every((n) => n.marks?.some((m) => m.type === 'code'));
    const bold = runs.length > 0 && runs.every((n) => n.marks?.some((m) => m.type === 'bold'));
    const toc = /^toc\s*\d|^оглавление\s*\d/.test(styleName) || /^MsoToc/i.test(cls);
    const docTitle = cls === 'MsoTitle' || /^(title|название)$/.test(styleName) || undefined;
    if (inl.length || list) {
      this.res.items.push({ k: 'p', inl, text, tag, hLevel, styleName, caption, list, align, mono, bold, toc, docTitle, el });
    } else if (!loose && !this.pendingImgs.length) this.res.dropped.empty++;
    this.flushImages();
  }

  // --- списки HTML
  private htmlList(el: Element) {
    const node = this.listNode(el);
    if (node && node.content?.length) {
      this.res.items.push({ k: 'list', node });
    }
    this.flushImages();
  }

  private listNode(el: Element): J | null {
    nestByAriaLevel(el);
    const tag = tagOf(el);
    const type = (el.getAttribute('type') || '').toLowerCase();
    const lst = (cssVal(styleOf(el), 'list-style-type') || '').toLowerCase();
    let kind = 'auto';
    if (tag === 'OL') kind = type === 'a' || type === 'а' || /alpha|cyrillic|latin/.test(lst) ? 'letter' : 'digit';
    else if (el.getAttribute('data-kind')) kind = el.getAttribute('data-kind')!;
    const items: J[] = [];
    for (const li of Array.from(el.children)) {
      if (tagOf(li) !== 'LI') {
        if (tagOf(li) === 'UL' || tagOf(li) === 'OL') {          // вложенный список без <li> (Google Docs, старые редакторы)
          const sub = this.listNode(li);
          const last = items[items.length - 1];
          if (sub && last) {
            // у позиции уже есть вложенный список - пункты дописываются в него (не второй список в одной позиции)
            const ex = last.content!.find((c) => c.type === 'stoList');
            if (ex) ex.content!.push(...(sub.content || []));
            else last.content!.push(sub);
          } else if (sub) items.push(...(sub.content || []));
        }
        continue;
      }
      if (isHidden(li)) continue;
      const inl: J[] = [];
      const subs: J[] = [];
      const walk = (n: Element) => {
        for (const ch of Array.from(n.childNodes)) {
          if (ch.nodeType === 1 && (tagOf(ch) === 'UL' || tagOf(ch) === 'OL')) {
            const s = this.listNode(ch as Element);
            if (s) subs.push(s);
          } else if (ch.nodeType === 1 && ['P', 'DIV'].includes(tagOf(ch)) && hasListChild(ch as Element)) {
            walk(ch as Element);
          } else if (ch.nodeType === 1 && (tagOf(ch) === 'TABLE' || tagOf(ch) === 'PRE')) {
            this.block(ch as Element, tagOf(ch));
          } else {
            if (ch.nodeType === 1 && ['P', 'DIV'].includes(tagOf(ch)) && inl.length) inl.push({ type: 'text', text: ' ' });
            this.inline(ch, {}, inl, false);
          }
        }
      };
      walk(li);
      trimInline(inl);
      const para: J = { type: 'paragraph' };
      if (inl.length) para.content = inl;
      const item: J = { type: 'stoListItem', content: [para] };
      // несколько вложенных списков подряд - в один (схема: paragraph stoList?)
      if (subs.length) item.content!.push(subs.length === 1 ? subs[0] : { ...subs[0], content: subs.flatMap((s) => s.content || []) });
      const al = Number(li.getAttribute('aria-level') || 0);
      const prev = items[items.length - 1];
      if (al > 1 && prev) {                                       // Google Docs: уровень через aria-level
        const host = deepestListItem(prev, al - 1);
        if (host) {
          const sl = host.content![1] || (host.content![1] = { type: 'stoList', attrs: { kind: 'auto' }, content: [] });
          sl.content!.push(item);
          continue;
        }
      }
      items.push(item);
    }
    if (!items.length) return null;
    return { type: 'stoList', attrs: { kind }, content: items };
  }

  // --- строчное содержимое
  inline(node: Node, marks: Marks, out: J[], inFootnote: boolean, onMarker?: (m: string) => void) {
    if (node.nodeType === 3) {
      let t = (node.textContent || '').replace(INVISIBLE_RE, '');
      t = t.replace(/[\t\n\r ]+/g, ' ');
      if (t) out.push(textNode(t, marks));
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node as Element;
    const tag = tagOf(el);
    if (SKIP_TAGS.has(tag) || this.skip.has(el)) return;
    const st = styleOf(el);
    if (/mso-list\s*:\s*ignore/i.test(st)) { onMarker?.((el.textContent || '').replace(/\u00a0/g, ' ').trim()); return; }
    if (isHidden(el)) { this.res.dropped.hidden++; return; }
    const math = mathOf(el);
    if (math) {
      const n: J = { type: 'mathInline', attrs: { latex: math.latex || '' } };
      (n as any).__math = math;
      this.res.maths.push(math);
      out.push(n);
      return;
    }
    // Википедия: ссылка на литературу [1]
    const cls = el.getAttribute('class') || '';
    if (tag === 'SUP' && /\breference\b/.test(cls)) {
      const a = el.querySelector('a[href^="#"]');
      const id = (a?.getAttribute('href') || '').slice(1);
      if (id && this.res.wikiRefs.has(id)) out.push({ type: 'text', text: '', __wikiRef: id } as any);
      return;
    }
    if (el.getAttribute('data-ole-eq') === '1') {
      // Equation 3.0 / MathType: картинку формулы не переносим рисунком - пустая формула «наберите заново»
      if (tag !== 'IMG') {
        const m: MathRef = { latex: '', display: false, error: 'ole' };
        const n: J = { type: 'mathInline', attrs: { latex: '' } };
        (n as any).__math = m;
        this.res.maths.push(m);
        this.res.oleEquations++;
        out.push(n);
      }
      return;
    }
    if (tag === 'IMG' || tag === 'V:IMAGEDATA') {
      const ref = imgRef(el);
      if (!ref) return;
      const small = (ref.width && ref.width <= 40 && (!ref.height || ref.height <= 40)) || (ref.height && ref.height <= 24 && (ref.width || 0) <= 60);
      if (small) { this.res.dropped.images++; return; }
      if (inFootnote) return;
      this.pendingImgs.push(ref);
      return;
    }
    if (tag === 'BR') {
      if (/page-break-before\s*:\s*always|mso-special-character\s*:\s*line-break|clear=all/i.test(st + (el.getAttribute('clear') ? ' clear=all' : ''))) return;
      out.push({ type: 'hardBreak' });
      return;
    }
    if (tag === 'A') {
      const target = inFootnote ? null : this.footnoteTarget(el);
      if (target) { out.push(this.footnoteNode(target)); return; }
    }
    const m: Marks = { ...marks };
    switch (tag) {
      case 'B': case 'STRONG': m.bold = true; break;
      case 'I': case 'EM': case 'CITE': case 'DFN': case 'VAR': m.italic = true; break;
      case 'U': case 'INS': m.underline = true; break;
      case 'SUP': if (!inFootnote || out.length) m.sup = true; delete m.sub; break;
      case 'SUB': m.sub = true; delete m.sup; break;
      case 'CODE': case 'KBD': case 'SAMP': case 'TT': m.code = true; break;
      case 'A': {
        const href = el.getAttribute('href') || '';
        if (/^(https?:|mailto:|ftp:)/i.test(href)) {
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text) this.res.links.push({ href, text });
          if (this.keepLinks) m.link = href;
        }
        break;
      }
    }
    // оформление через style (Google Docs, LibreOffice, веб): только смысловые метки
    const fw = cssVal(st, 'font-weight');
    if (fw) m.bold = /bold|[6-9]00/.test(fw) ? true : /normal|lighter|[1-5]00/.test(fw) ? false : m.bold;
    const fs = cssVal(st, 'font-style');
    if (fs) m.italic = /italic|oblique/.test(fs) ? true : /normal/.test(fs) ? false : m.italic;
    const td = cssVal(st, 'text-decoration') || cssVal(st, 'text-decoration-line');
    if (td && /underline/.test(td)) m.underline = true;
    const va = cssVal(st, 'vertical-align');
    if (va === 'super') { m.sup = true; delete m.sub; } else if (va === 'sub') { m.sub = true; delete m.sup; }
    const ff = cssVal(st, 'font-family');
    if (ff && !/inherit/i.test(ff)) m.code = MONO_RE.test(ff);
    if (/mso-tab-count|mso-spacerun/i.test(st)) { out.push(textNode(' ', m)); return; }
    for (const ch of Array.from(el.childNodes)) this.inline(ch, m, out, inFootnote, onMarker);
  }
}

// ------------------------------------------------------------------------------------------------ помощники
/** Google Документы и др.: плоский список <li aria-level="1|2|3"> -> вложенные <ul> (уровень - из aria-level). */
function nestByAriaLevel(list: Element) {
  const lis = Array.from(list.children).filter((c) => tagOf(c) === 'LI');
  if (!lis.some((li) => Number(li.getAttribute('aria-level') || 1) > 1)) return;
  if (lis.some((li) => li.querySelector(':scope > ul, :scope > ol'))) return;    // уже вложенный
  const doc = list.ownerDocument;
  const stack: { level: number; el: Element }[] = [{ level: Number(lis[0].getAttribute('aria-level') || 1), el: list }];
  for (const li of lis) {
    const lvl = Math.max(1, Number(li.getAttribute('aria-level') || 1));
    while (stack.length > 1 && lvl < stack[stack.length - 1].level) stack.pop();
    let top = stack[stack.length - 1];
    if (lvl > top.level) {
      let parentLi: Element | null = top.el === list ? li.previousElementSibling : top.el.lastElementChild;
      while (parentLi && tagOf(parentLi) !== 'LI') parentLi = parentLi.previousElementSibling;
      if (parentLi) {
        const sub = doc.createElement(list.tagName.toLowerCase());
        parentLi.appendChild(sub);
        stack.push({ level: lvl, el: sub });
        top = stack[stack.length - 1];
      }
    }
    if (top.el !== list) top.el.appendChild(li);
  }
}

/** Ячейки таблицы без вложенных таблиц. */
export function tableCells(el: HTMLTableElement): Element[] {
  const out: Element[] = [];
  const rows: Element[] = [];
  for (const ch of Array.from(el.children)) {
    if (ch.tagName === 'TR') rows.push(ch);
    else if (['THEAD', 'TBODY', 'TFOOT'].includes(ch.tagName)) rows.push(...Array.from(ch.children).filter((r) => r.tagName === 'TR'));
  }
  for (const r of rows) for (const c of Array.from(r.children)) if (c.tagName === 'TD' || c.tagName === 'TH') out.push(c);
  return out;
}

function hasBlockChild(el: Element): boolean {
  for (const ch of Array.from(el.children)) {
    const t = tagOf(ch);
    if (BLOCK_TAGS.has(t)) return true;
    if (t === 'SPAN' || t === 'FONT' || t === 'A' || t === 'B' || t === 'I') { if (hasBlockChild(ch)) return true; }
    if ((t === 'DIV' || t === 'SPAN') && mathOf(ch)?.display) return true;
  }
  return false;
}

function hasListChild(el: Element): boolean {
  return !!el.querySelector(':scope > ul, :scope > ol');
}

function isStandaloneImage(el: Element): boolean {
  const t = tagOf(el);
  if (t === 'IMG') return true;
  if (t === 'A' || t === 'SPAN' || t === 'PICTURE') {
    return !!el.querySelector('img') && !(el.textContent || '').trim();
  }
  return false;
}

function deepestListItem(item: J, depth: number): J | null {
  let cur = item;
  for (let d = 1; d < depth; d++) {
    const sub = cur.content?.[1];
    if (!sub?.content?.length) return cur;
    cur = sub.content[sub.content.length - 1];
  }
  return cur;
}

function imgRef(el: Element): ImgRef | null {
  let src = el.getAttribute('src') || el.getAttribute('data-src') || '';
  const srcset = el.getAttribute('srcset');
  if ((!src || src.startsWith('data:image/gif')) && srcset) src = srcset.split(',')[0].trim().split(/\s+/)[0];
  if (!src) return null;
  if (src.startsWith('//')) src = 'https:' + src;
  return { src, width: numAttr(el, 'width'), height: numAttr(el, 'height'), alt: el.getAttribute('alt') || el.getAttribute('o:title') || '' };
}

function preText(el: Element): string {
  if (tagOf(el) === 'PRE') return (el.textContent || '').replace(/\r\n?/g, '\n').replace(/\n$/, '');
  const lines: string[] = [];
  let cur = '';
  const walk = (n: Node) => {
    if (n.nodeType === 3) { cur += n.textContent || ''; return; }
    if (n.nodeType !== 1) return;
    const t = tagOf(n);
    if (t === 'BR') { lines.push(cur); cur = ''; return; }
    const block = BLOCK_TAGS.has(t);
    if (block && cur) { lines.push(cur); cur = ''; }
    for (const c of Array.from(n.childNodes)) walk(c);
    if (block) { lines.push(cur); cur = ''; }
  };
  walk(el);
  if (cur) lines.push(cur);
  return lines.join('\n').replace(/\u00a0/g, ' ').replace(/^\n+|\n+$/g, '');
}

function codeLang(el: Element): string {
  const probe = [el, el.querySelector('code')].filter(Boolean) as Element[];
  for (const e of probe) {
    const c = (e.getAttribute('class') || '') + ' ' + (e.getAttribute('data-lang') || '') + ' ' + (e.getAttribute('data-language') || '');
    const m = c.match(/(?:language|lang|brush:?)[-_\s]?([\w+#-]+)/i) || c.match(/^\s*([\w+#-]+)\s*$/);
    if (m && m[1]) return normLang(m[1]);
  }
  return '';
}

export function normLang(s: string): string {
  const l = s.toLowerCase();
  const map: Record<string, string> = { py: 'python', python3: 'python', js: 'javascript', ts: 'typescript', 'c++': 'cpp',
    cxx: 'cpp', h: 'c', hpp: 'cpp', sh: 'bash', shell: 'bash', shellscript: 'bash', zsh: 'bash', ps1: 'powershell',
    bat: 'bat', cmd: 'bat', m: 'matlab', octave: 'matlab', pas: 'pascal', delphi: 'pascal', 'objective-c': 'c', 'c#': 'csharp', cs: 'csharp' };
  return map[l] || l;
}

export function textNode(t: string, m: Marks): J {
  const marks: J['marks'] = [];
  if (m.bold) marks.push({ type: 'bold' });
  if (m.italic) marks.push({ type: 'italic' });
  if (m.underline) marks.push({ type: 'underline' });
  if (m.sup) marks.push({ type: 'superscript' });
  if (m.sub) marks.push({ type: 'subscript' });
  if (m.code) marks.push({ type: 'code' });
  if (m.link) marks.push({ type: 'link', attrs: { href: m.link } });
  const n: J = { type: 'text', text: t };
  if (marks.length) n.marks = marks;
  return n;
}

const sameMarks = (a: J, b: J) => JSON.stringify(a.marks || []) === JSON.stringify(b.marks || []);

/** Склеить соседние тексты с одинаковыми метками, схлопнуть пробелы, обрезать края; пустые - убрать. */
export function trimInline(inl: J[]) {
  const out: J[] = [];
  for (const n of inl) {
    if (n.type === 'text' && !(n as any).__wikiRef) {
      if (!n.text) continue;
      const prev = out[out.length - 1];
      if (prev && prev.type === 'text' && !(prev as any).__wikiRef && sameMarks(prev, n)) { prev.text += n.text; continue; }
      // пробел между разными метками - в предыдущий текст (без метки кода/ссылки)
    }
    out.push(n);
  }
  for (const n of out) if (n.type === 'text' && n.text) n.text = n.text.replace(/ {2,}/g, ' ');
  // пробелы на стыках: «слово » + « слово» -> один
  for (let i = 1; i < out.length; i++) {
    const a = out[i - 1];
    const b = out[i];
    if (a.type === 'text' && b.type === 'text' && a.text?.endsWith(' ') && b.text?.startsWith(' ')) b.text = b.text.slice(1);
  }
  // края абзаца
  while (out.length && out[0].type === 'text' && !(out[0].text || '').replace(/^[\s\u00a0]+/, '') && !(out[0] as any).__wikiRef) out.shift();
  while (out.length && out[out.length - 1].type === 'hardBreak') out.pop();
  while (out.length && out[out.length - 1].type === 'text' && !(out[out.length - 1].text || '').replace(/[\s\u00a0]+$/, '') && !(out[out.length - 1] as any).__wikiRef) out.pop();
  if (out.length && out[0].type === 'text') out[0].text = out[0].text!.replace(/^[\s\u00a0]+/, '');
  const last = out[out.length - 1];
  if (last && last.type === 'text') last.text = last.text!.replace(/[\s\u00a0]+$/, '');
  inl.splice(0, inl.length, ...out.filter((n) => n.type !== 'text' || n.text || (n as any).__wikiRef));
}

/** Плоский текст строчного содержимого (формулы - как ⟨ф⟩, сноски - пусто). */
export function inlineText(inl: J[]): string {
  return inl.map((n) => (n.type === 'text' ? n.text || '' : n.type === 'mathInline' ? '\u2062' : n.type === 'hardBreak' ? ' ' : '')).join('');
}
